import React, { useState, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Nav, NavItem, NavLink, TabContent, TabPane, Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { Translate } from '../../contexts/Language';
import { Card, BreadCrumb } from '../../components';
import { DepositTab } from './UniPayment/DepositTab';
import { WithdrawalTab } from './UniPayment/WithdrawalTab';
import { Service } from '../../services';


const UniPayment = () => {
  const [activeTab, setActiveTab] = useState('1');
  const [balances, setBalances] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const fetchWalletData = useCallback(() => {
    // Fetch balances
    Service.UniPayment.GetWalletBalances().then(response => {
      if (response && response.error === false) {
        setBalances(response.data || []);
      } else {
        console.error('Failed to fetch balances:', response.message);
        Swal.fire({
          icon: 'warning',
          title: Translate('Warning'),
          text: response.message || Translate('Failed to fetch wallet balances'),
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }).catch(error => {
      console.error('Error fetching balances:', error);
    });


    // Fetch accounts
    Service.UniPayment.GetWalletAccounts().then(response => {
      if (response && response.error === false) {
        const accountsList = response.data || [];
        setAccounts(accountsList);
        // Auto-select first account if available
        if (accountsList.length > 0 && !selectedAccount) {
          setSelectedAccount(accountsList[0].id || accountsList[0].account_id);
        }
      } else {
        console.error('Failed to fetch accounts:', response.message);
        Swal.fire({
          icon: 'warning',
          title: Translate('Warning'),
          text: response.message || Translate('Failed to fetch wallet accounts'),
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }).catch(error => {
      console.error('Error fetching accounts:', error);
    });
  }, [selectedAccount]);

  // Fetch wallet data
  useEffect(() => {
    fetchWalletData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Handler functions
  const toggleTab = useCallback(tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  }, [activeTab]);

  return (
    <>
      <BreadCrumb title={Translate('UniPayment')} />

      {/* Wallet Balances Dashboard */}
      <Row className="mb-4">
        <Col md={12}>
          <Card title={Translate('Wallet Balances')}>
            <Row>
              {balances && balances.length > 0 ? (
                balances.map((balance, index) => (
                  <Col md={3} key={index} className="mb-3">
                    <div style={{
                      borderLeft: '4px solid #007bff',
                      border: 'none',
                      WebkitBoxShadow: '0px 0px 13px 0px rgba(236, 236, 241, 0.44)',
                      boxShadow: '0px 0px 13px 0px rgba(236, 236, 241, 0.44)',
                      marginBottom: '5px',
                    }}
                    >
                      <div style={{ flex: '1 1 auto', padding: '0.25rem' }}>
                        <h6 className="text-muted mb-2 text-small">{balance.currency || balance.asset_type}</h6>
                        <h6 className="mb-0">{parseFloat(balance.balance || balance.available || 0).toFixed(2)}</h6>
                        {balance.pending && (
                          <small className="text-muted">
                            {Translate('Pending')}
:
                            {parseFloat(balance.pending).toFixed(2)}
                          </small>
                        )}
                      </div>
                    </div>
                  </Col>
                ))
              ) : (
                <Col md={12}>
                  <p className="text-muted text-center">{Translate('No balances available')}</p>
                </Col>
              )}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Account Selection */}
      <Row className="mb-4">
        <Col md={4}>
          <FormGroup>
            <Label>
              {Translate('Select Account')}
              {' '}
              <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={selectedAccount}
              onChange={e => setSelectedAccount(e.target.value)}
            >
              <option value="">{Translate('Select an account')}</option>
              {accounts.map(account => (
                <option key={account.id || account.account_id} value={account.id || account.account_id}>
                  {account.friendly_name || account.account_number}
                  {' '}
-
                  {account.asset_type}
                  {' '}
-
                  {account.available || account.balance}
                </option>
              ))}
            </Input>
            <small className="text-muted">{Translate('This account will be used for payment creation')}</small>
          </FormGroup>
        </Col>
      </Row>

      <Card title={Translate('UniPayment Management')}>
        <Nav tabs>
          <NavItem>
            <NavLink
              className={activeTab === '1' ? 'active' : ''}
              onClick={() => toggleTab('1')}
              style={{ cursor: 'pointer' }}
            >
              {Translate('Deposits')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === '2' ? 'active' : ''}
              onClick={() => toggleTab('2')}
              style={{ cursor: 'pointer' }}
            >
              {Translate('Withdrawals')}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <DepositTab />
          </TabPane>

          <TabPane tabId="2">
            <WithdrawalTab
              selectedAccount={selectedAccount}
              accountData={accounts.find(acc => (acc.id || acc.account_id) === selectedAccount) || {}}
            />
          </TabPane>
        </TabContent>
      </Card>
    </>
  );
};

export default UniPayment;
